/**
 * Comprehensive keyboard shortcut system with combo support and platform-aware
 * display (Mac vs Windows/Linux).
 *
 * Storage format (per shortcut): a canonical combo string
 *   "<mods>+<KeyCode>" where mods is a subset of [ctrl, shift, alt, meta]
 *   in canonical order. Examples:
 *     "KeyR"               -> just R
 *     "ctrl+KeyR"          -> Ctrl + R
 *     "ctrl+shift+KeyR"    -> Ctrl + Shift + R
 *     "meta+Slash"         -> ⌘ + /  (Mac)
 *
 * Backward compat: legacy storage values like "Space", "KeyR", "Escape" remain
 * valid (no modifier).
 */

const STORAGE_KEY = "wffa-keyboard-shortcuts-v2";
const LEGACY_TIMER_KEY = "wffa-timer-shortcuts-v1";

const MOD_ORDER = ["ctrl", "shift", "alt", "meta"];

const MODIFIER_CODES = new Set([
  "ShiftLeft", "ShiftRight",
  "ControlLeft", "ControlRight",
  "AltLeft", "AltRight",
  "MetaLeft", "MetaRight",
  "OSLeft", "OSRight",
]);

export const isMacPlatform = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const platform = navigator.platform || "";
  return /Mac|iPhone|iPad|iPod/.test(platform) || /Mac OS X/.test(ua);
};

export const DEFAULT_SHORTCUTS = {
  // --- Timer (legacy keys: play/stop/reset/escape) ---
  play: "Space",
  stop: "Enter",
  reset: "KeyR",
  escape: "Escape",
  testSound: "KeyT",

  // --- Battle / Circle control ---
  startEvent: "KeyB",
  revealJ1: "Digit1",
  revealJ2: "Digit2",
  revealJ3: "Digit3",
  revealJ4: "Digit4",
  revealJ5: "Digit5",
  revealJ6: "Digit6",
  revealJ7: "Digit7",
  revealJ8: "Digit8",
  revealJ9: "Digit9",
  revealJ10: "Digit0",
  revealAll: "KeyA",
  revealNext: "KeyN",
  finishEvent: "KeyF",
  resetEvent: "KeyX",
  forfeitA1: "KeyQ",
  forfeitA2: "KeyW",

  // --- Mode toggles ---
  toggleCountdown: "KeyC",
  toggleSimulation: "KeyS",
  nextEvent: "KeyG",

  // --- Tab navigation ---
  tabAthletes: "F1",
  tabJudges: "F2",
  tabTournament: "F3",
  tabControl: "F4",
  tabSettings: "F5",
};

export const SHORTCUT_GROUPS = [
  {
    id: "battle",
    titleKey: "shortcuts.groupBattle",
    actions: [
      "startEvent",
      "revealJ1", "revealJ2", "revealJ3", "revealJ4", "revealJ5",
      "revealJ6", "revealJ7", "revealJ8", "revealJ9", "revealJ10",
      "revealNext", "revealAll", "finishEvent", "resetEvent",
      "forfeitA1", "forfeitA2",
    ],
  },
  {
    id: "timer",
    titleKey: "shortcuts.groupTimer",
    actions: ["play", "stop", "reset", "escape", "testSound"],
  },
  {
    id: "mode",
    titleKey: "shortcuts.groupMode",
    actions: ["toggleCountdown", "toggleSimulation", "nextEvent"],
  },
  {
    id: "nav",
    titleKey: "shortcuts.groupNav",
    actions: ["tabAthletes", "tabJudges", "tabTournament", "tabControl", "tabSettings"],
  },
];

export const ACTION_LABEL_KEYS = {
  // timer
  play: "shortcuts.actionPlayPause",
  stop: "shortcuts.actionStopNext",
  reset: "shortcuts.actionResetTimer",
  escape: "shortcuts.actionEmergencyStop",
  testSound: "shortcuts.actionTestSound",
  // battle
  startEvent: "shortcuts.actionStartEvent",
  revealJ1: "shortcuts.actionRevealJ1",
  revealJ2: "shortcuts.actionRevealJ2",
  revealJ3: "shortcuts.actionRevealJ3",
  revealJ4: "shortcuts.actionRevealJ4",
  revealJ5: "shortcuts.actionRevealJ5",
  revealJ6: "shortcuts.actionRevealJ6",
  revealJ7: "shortcuts.actionRevealJ7",
  revealJ8: "shortcuts.actionRevealJ8",
  revealJ9: "shortcuts.actionRevealJ9",
  revealJ10: "shortcuts.actionRevealJ10",
  revealNext: "shortcuts.actionRevealNext",
  revealAll: "shortcuts.actionRevealAll",
  finishEvent: "shortcuts.actionFinishEvent",
  resetEvent: "shortcuts.actionResetEvent",
  forfeitA1: "shortcuts.actionForfeitA1",
  forfeitA2: "shortcuts.actionForfeitA2",
  // mode
  toggleCountdown: "shortcuts.actionToggleCountdown",
  toggleSimulation: "shortcuts.actionToggleSimulation",
  nextEvent: "shortcuts.actionNextEvent",
  // nav
  tabAthletes: "shortcuts.actionTabAthletes",
  tabJudges: "shortcuts.actionTabJudges",
  tabTournament: "shortcuts.actionTabTournament",
  tabControl: "shortcuts.actionTabControl",
  tabSettings: "shortcuts.actionTabSettings",
};

/** Build canonical combo string from a KeyboardEvent. */
export const buildComboFromEvent = (e) => {
  if (!e || !e.code || MODIFIER_CODES.has(e.code)) return null;
  const parts = [];
  if (e.ctrlKey) parts.push("ctrl");
  if (e.shiftKey) parts.push("shift");
  if (e.altKey) parts.push("alt");
  if (e.metaKey) parts.push("meta");
  parts.push(e.code);
  return parts.join("+");
};

/** Parse a stored combo string into { mods: Set, code }. */
export const parseCombo = (combo) => {
  if (!combo) return { mods: new Set(), code: "" };
  const parts = combo.split("+");
  const code = parts[parts.length - 1];
  const mods = new Set(parts.slice(0, -1));
  return { mods, code };
};

const formatSingleKey = (code) => {
  if (!code) return "—";
  if (code === "Space") return "Espace";
  if (code === "Enter") return "Entrée";
  if (code === "Escape") return "Esc";
  if (code === "Backspace") return "←";
  if (code === "Tab") return "Tab";
  if (code === "Slash") return "/";
  if (code === "Backslash") return "\\";
  if (code === "Minus") return "-";
  if (code === "Equal") return "=";
  if (code === "Comma") return ",";
  if (code === "Period") return ".";
  if (code === "Semicolon") return ";";
  if (code === "Quote") return "'";
  if (code === "BracketLeft") return "[";
  if (code === "BracketRight") return "]";
  if (code === "Backquote") return "`";
  if (code.startsWith("Key")) return code.slice(3);
  if (code.startsWith("Digit")) return code.slice(5);
  if (code.startsWith("Numpad")) return "Num " + code.slice(6);
  if (code.startsWith("Arrow")) return "↑↓←→".charAt(["Up","Down","Left","Right"].indexOf(code.slice(5))) || code;
  if (/^F\d{1,2}$/.test(code)) return code;
  return code;
};

const MOD_LABELS_MAC = { ctrl: "⌃", shift: "⇧", alt: "⌥", meta: "⌘" };
const MOD_LABELS_WIN = { ctrl: "Ctrl", shift: "Shift", alt: "Alt", meta: "Win" };
const MOD_SEP_MAC = "";
const MOD_SEP_WIN = "+";

/** Format a combo string for display, platform-aware. */
export const formatCombo = (combo, mac = isMacPlatform()) => {
  if (!combo) return "—";
  const { mods, code } = parseCombo(combo);
  const labels = mac ? MOD_LABELS_MAC : MOD_LABELS_WIN;
  const sep = mac ? MOD_SEP_MAC : MOD_SEP_WIN;
  const orderedMods = MOD_ORDER.filter((m) => mods.has(m)).map((m) => labels[m]);
  const keyLabel = formatSingleKey(code);
  if (orderedMods.length === 0) return keyLabel;
  if (mac) return orderedMods.join("") + keyLabel;
  return [...orderedMods, keyLabel].join(sep);
};

/** Backward-compatible alias used by older imports. */
export const formatKeyCode = (combo) => formatCombo(combo);

export const loadShortcuts = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      const legacy = localStorage.getItem(LEGACY_TIMER_KEY);
      if (legacy) {
        const legacyParsed = JSON.parse(legacy);
        return { ...DEFAULT_SHORTCUTS, ...legacyParsed };
      }
      return { ...DEFAULT_SHORTCUTS };
    }
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_SHORTCUTS, ...parsed };
  } catch {
    return { ...DEFAULT_SHORTCUTS };
  }
};

export const saveShortcuts = (sc) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sc));
    // Keep legacy bag in sync (timer panels still read from it).
    const legacy = {
      play: sc.play,
      stop: sc.stop,
      reset: sc.reset,
      escape: sc.escape,
    };
    localStorage.setItem(LEGACY_TIMER_KEY, JSON.stringify(legacy));
  } catch {
    /* ignore */
  }
};

export const findConflicts = (shortcuts) => {
  const byCombo = {};
  for (const [action, combo] of Object.entries(shortcuts || {})) {
    if (!combo) continue;
    byCombo[combo] = byCombo[combo] || [];
    byCombo[combo].push(action);
  }
  const conflicts = {};
  for (const [, actions] of Object.entries(byCombo)) {
    if (actions.length > 1) {
      for (const a of actions) conflicts[a] = actions;
    }
  }
  return conflicts;
};

/**
 * Install a global keydown listener that triggers the matching handler.
 * `handlers` is { actionId: () => void }. Returns an unsubscribe function.
 *
 * The listener compares each event's full combo (modifiers + code) against
 * the saved shortcut value.
 */
export const installShortcutListeners = (handlers) => {
  const sc = loadShortcuts();
  const onKey = (e) => {
    const tgt = e.target;
    if (
      tgt &&
      (tgt.tagName === "INPUT" ||
        tgt.tagName === "TEXTAREA" ||
        tgt.tagName === "SELECT" ||
        tgt.isContentEditable)
    ) {
      return;
    }
    const combo = buildComboFromEvent(e);
    if (!combo) return;
    for (const action of Object.keys(handlers)) {
      if (sc[action] === combo) {
        e.preventDefault();
        try {
          handlers[action]();
        } catch {
          /* ignore */
        }
        return;
      }
    }
  };
  window.addEventListener("keydown", onKey);
  return () => window.removeEventListener("keydown", onKey);
};
